import { z } from 'zod';
import { ERROR_CODE_PATTERN, isErrorCode, type ErrorCode } from './error-codes';

/**
 * Strict, size-limited schema for critical non-personal browser errors sent to
 * POST /api/diagnostics/client-errors. Free text is limited; unknown keys are rejected;
 * only codes present in the registry are accepted.
 */
export const CLIENT_ERROR_MAX_BYTES = 4096;

export const clientErrorReportSchema = z
  .object({
    code: z.custom<ErrorCode>(isErrorCode, { message: 'Unregistered error code' }),
    severity: z.enum(['ERROR', 'CRITICAL']),
    module: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z0-9-]+$/),
    message: z.string().min(1).max(300),
    correlationId: z.string().min(1).max(100),
    appVersion: z.string().max(40),
    route: z
      .string()
      .max(120)
      .regex(/^\/[A-Za-z0-9/_-]*$/),
    occurredAt: z.iso.datetime(),
  })
  .strict();
export type ClientErrorReport = z.infer<typeof clientErrorReportSchema>;

export const healthResponseSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  version: z.string(),
  uptimeSeconds: z.number().nonnegative(),
  database: z.enum(['up', 'down', 'not-configured']),
  requestId: z.string(),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

/** Every API error response has this shape: stable code, user-safe message, correlation ID. */
export const apiErrorResponseSchema = z.object({
  error: z.object({
    code: z.string().regex(ERROR_CODE_PATTERN),
    message: z.string(),
    requestId: z.string(),
    issues: z.array(z.object({ path: z.string(), problem: z.string() })).optional(),
  }),
});
export type ApiErrorResponse = z.infer<typeof apiErrorResponseSchema>;
